import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
    FolderOpen, Search, Image, FileText, Video, ChevronRight, ChevronDown,
    Loader2, X, Check
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getFileIcon(type) {
    switch (type) {
        case 'image': return Image;
        case 'video': return Video;
        default: return FileText;
    }
}

function getTypeColor(type) {
    switch (type) {
        case 'image': return 'text-blue-600 bg-blue-50';
        case 'video': return 'text-purple-600 bg-purple-50';
        case 'document': return 'text-amber-600 bg-amber-50';
        default: return 'text-stone-600 bg-stone-50';
    }
}

function formatFileSize(bytes) {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getVideoEmbedUrl(url, provider) {
    if (!url) return null;
    if (provider === 'youtube') {
        const match = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        return match ? `https://www.youtube.com/embed/${match[1]}` : null;
    }
    if (provider === 'loom') {
        const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
        return match ? `https://www.loom.com/embed/${match[1]}` : null;
    }
    if (provider === 'vimeo') {
        const match = url.match(/vimeo\.com\/(\d+)/);
        return match ? `https://player.vimeo.com/video/${match[1]}` : null;
    }
    return null;
}

// Compact folder tree for picker
function PickerFolderTree({ folders, selectedFolder, onSelect }) {
    const [expanded, setExpanded] = useState({});

    const tree = {};
    folders.forEach(f => {
        // Backend returns {name, count, children[]} objects (not flat {path, count})
        if (f.children) {
            // Already structured as a tree node from backend
            tree[f.name] = { path: f.name, count: f.count || 0, children: (f.children || []).map(c => ({ path: c.path || c.name, name: c.name, count: c.count || 0 })) };
        } else if (f.path) {
            const parts = f.path.split('/');
            const top = parts[0];
            if (!tree[top]) tree[top] = { path: top, count: 0, children: [] };
            tree[top].count += f.count || 0;
            if (parts.length > 1) {
                tree[top].children.push({ path: f.path, name: parts[1], count: f.count || 0 });
            }
        }
    });

    return (
        <div className="space-y-0.5">
            <button
                onClick={() => onSelect(null)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                    selectedFolder === null ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-100'
                }`}
            >
                <FolderOpen className="w-3.5 h-3.5" />
                All Assets
            </button>
            {Object.entries(tree).sort(([a], [b]) => a.localeCompare(b)).map(([name, folder]) => {
                const isExpanded = expanded[name];
                return (
                    <div key={name}>
                        <button
                            onClick={() => {
                                setExpanded(e => ({ ...e, [name]: !e[name] }));
                                onSelect(folder.path);
                            }}
                            className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                                selectedFolder === folder.path ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-100'
                            }`}
                        >
                            {folder.children.length > 0 ? (
                                isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />
                            ) : <span className="w-3" />}
                            <FolderOpen className="w-3.5 h-3.5" />
                            <span className="truncate flex-1 text-left">{name}</span>
                            {folder.count > 0 && (
                                <span className="text-[10px] opacity-60">{folder.count}</span>
                            )}
                        </button>
                        {isExpanded && folder.children.sort((a, b) => a.name.localeCompare(b.name)).map(child => (
                            <button
                                key={child.path}
                                onClick={() => onSelect(child.path)}
                                className={`w-full flex items-center gap-1.5 pl-7 pr-2 py-1 rounded text-xs transition-colors ${
                                    selectedFolder === child.path ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'
                                }`}
                            >
                                <span className="truncate flex-1 text-left">{child.name}</span>
                                {child.count > 0 && (
                                    <span className="text-[10px] opacity-60">{child.count}</span>
                                )}
                            </button>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

export default function ContentPicker({ open, onOpenChange, onSelect, allowedTypes, mode = 'insert' }) {
    const { token } = useAuth();
    const [assets, setAssets] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState(null);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selected, setSelected] = useState(null);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchAssets = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (selectedFolder) params.append('folder', selectedFolder);
            if (search) params.append('search', search);
            if (typeFilter) params.append('file_type', typeFilter);
            const res = await axios.get(`${API}/content-library/picker/assets?${params}`, { headers });
            setAssets(res.data || []);
        } catch (err) {
            console.error('Failed to load assets', err);
        } finally {
            setLoading(false);
        }
    }, [open, selectedFolder, search, typeFilter, token]);

    const fetchFolders = useCallback(async () => {
        if (!open) return;
        try {
            const res = await axios.get(`${API}/content-library/folders`, { headers });
            setFolders(res.data || []);
        } catch (err) {
            console.error('Failed to load folders', err);
        }
    }, [open, token]);

    useEffect(() => { fetchFolders(); }, [fetchFolders]);
    useEffect(() => { fetchAssets(); }, [fetchAssets]);

    // Reset state when opened
    useEffect(() => {
        if (open) {
            setSelected(null);
            setSearch('');
            setTypeFilter(allowedTypes?.length === 1 ? allowedTypes[0] : null);
        }
    }, [open, allowedTypes]);

    const handleSelect = (asset) => {
        setSelected(asset.asset_id === selected?.asset_id ? null : asset);
    };

    const handleConfirm = () => {
        if (!selected) return;
        const url = selected.url || selected.s3_url || selected.external_url || '';
        let html = '';
        if (selected.file_type === 'image') {
            html = `<img src="${url}" alt="${selected.name}" style="max-width:100%;height:auto;" />`;
        } else if (selected.file_type === 'video' && selected.external_url) {
            const embedUrl = getVideoEmbedUrl(selected.external_url, selected.video_provider);
            if (embedUrl) {
                html = `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;"><iframe src="${embedUrl}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allowfullscreen></iframe></div>`;
            } else {
                html = `<a href="${selected.external_url}" target="_blank" rel="noopener">${selected.name}</a>`;
            }
        } else {
            html = `<a href="${url}" target="_blank" rel="noopener">${selected.name}</a>`;
        }
        onSelect({ url, name: selected.name, type: selected.file_type, asset_id: selected.asset_id, html });
        onOpenChange(false);
    };

    const typeButtons = allowedTypes || ['image', 'document', 'video'];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col p-0">
                <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-200">
                    <DialogTitle className="text-base font-semibold">Content Library</DialogTitle>
                    <DialogDescription className="text-xs text-stone-500">
                        {mode === 'insert' ? 'Select an asset to insert into your content' : 'Select an asset'}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-1 min-h-0">
                    {/* Sidebar */}
                    <div className="w-48 border-r border-stone-200 p-3 overflow-y-auto flex-shrink-0">
                        <PickerFolderTree
                            folders={folders}
                            selectedFolder={selectedFolder}
                            onSelect={setSelectedFolder}
                        />
                    </div>

                    {/* Main */}
                    <div className="flex-1 flex flex-col min-w-0">
                        {/* Filters */}
                        <div className="flex items-center gap-2 p-3 border-b border-stone-100">
                            <div className="relative flex-1">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search assets..."
                                    className="pl-8 h-8 text-xs"
                                />
                            </div>
                            <div className="flex gap-1">
                                {typeButtons.map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setTypeFilter(typeFilter === t ? null : t)}
                                        className={`px-2 py-1 rounded text-[10px] font-medium capitalize transition-colors ${
                                            typeFilter === t ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                        }`}
                                    >
                                        {t === 'document' ? 'Docs' : t + 's'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Asset grid */}
                        <div className="flex-1 overflow-y-auto p-3">
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                                </div>
                            ) : assets.length === 0 ? (
                                <div className="text-center py-12 text-stone-400 text-sm">
                                    No assets found
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 gap-2">
                                    {assets.map(asset => {
                                        const Icon = getFileIcon(asset.file_type);
                                        const isSelected = selected?.asset_id === asset.asset_id;
                                        return (
                                            <button
                                                key={asset.asset_id}
                                                onClick={() => handleSelect(asset)}
                                                className={`relative flex flex-col rounded-lg border-2 p-2 text-left transition-all hover:shadow-sm ${
                                                    isSelected
                                                        ? 'border-stone-900 bg-stone-50 ring-1 ring-stone-900'
                                                        : 'border-stone-200 hover:border-stone-300'
                                                }`}
                                            >
                                                {isSelected && (
                                                    <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-stone-900 rounded-full flex items-center justify-center">
                                                        <Check className="w-3 h-3 text-white" />
                                                    </div>
                                                )}
                                                {/* Preview */}
                                                <div className="w-full aspect-square rounded bg-stone-100 flex items-center justify-center mb-2 overflow-hidden">
                                                    {asset.file_type === 'image' && (asset.url || asset.s3_url) ? (
                                                        <img src={asset.url || asset.s3_url} alt={asset.name} className="w-full h-full object-cover rounded" />
                                                    ) : (
                                                        <div className={`p-3 rounded-lg ${getTypeColor(asset.file_type)}`}>
                                                            <Icon className="w-6 h-6" />
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-xs font-medium text-stone-800 truncate">{asset.name}</p>
                                                <p className="text-[10px] text-stone-400 truncate">
                                                    {asset.file_type}{asset.file_size ? ` \u00B7 ${formatFileSize(asset.file_size)}` : ''}
                                                </p>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-3 border-t border-stone-200 bg-stone-50">
                    <p className="text-xs text-stone-500">
                        {selected ? `Selected: ${selected.name}` : 'Click an asset to select it'}
                    </p>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button size="sm" disabled={!selected} onClick={handleConfirm}>
                            {mode === 'insert' ? 'Insert' : 'Select'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
